import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import compose from 'lodash/fp/compose';
import {
  withDocument,
  withTranslation,
  withPersonalInfo,
  withCustomRouter,
  withUserDefaultSpace,
} from '../../hoc';
import routes, { redirects } from './routes';
import { getResError, setMsgInfo } from 'actions/';
import Modal from 'components/common/Modal';
import filterError from 'actions/common/filterError';
import Button from 'components/common/FormItems/Button';
import { documentSlice } from 'rootStateHandler/selectors';
import {
  documentDetailsThunk,
  fullTextSearchThunk,
  listDocumentsThunk,
  documentClearDetailsThunk,
} from './documentStates/documentThunk';
import {
  deleteDocumentService,
  extractPostboxDocuments,
  listPostboxCredentials,
} from './services/documentServices';
import DocumentForm from './Form';
import Dropzone from './Dropzone';
import DocumentInfo from './DocumentInfo';
import ListDocuments from './ListDocuments';
import FilterCriteria from './FilterCriteria';
import styled from 'styled-components';
import DashboardTemplate, {
  getHasSideBarStyle,
} from '../../common/DashboardTemplate';
import './index.css';
import './index.css';
import { Dropdown } from 'components/common/ContentTemplate/TableBody.js';
import API from '../../../actions/api';
import axios from '../../../axios';
import PostboxConfiguration, {
  PostboxExtractionSummary,
} from './PostboxConfiguration';

const MainWrapper = styled.div`
  width: 100%;
  flex: 1;
  ${getHasSideBarStyle}
`;

const Container = styled.div`
  margin-left: 50px;
`;

const Documents = (props) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [documentId, setDocumentId] = useState();
  const [isSearching, setIsSearching] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [toggleModal, setToggleModal] = useState(false);
  const [toggleViewDocumentModal, setToggleViewDocumentModalModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isExtractingPostboxes, setIsExtractingPostboxes] = useState(false);
  const [exportedDocumentUrl, setExportedDocumentUrl] = useState(null);
  const [showPostboxConfiguration, setShowPostboxConfiguration] = useState(false);
  const [showPostboxSummary, setShowPostboxSummary] = useState(false);
  const [postboxResults, setPostboxResults] = useState([]);
  const { documentDetails } = useSelector(documentSlice);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    toggleDocumentForm,
    tr,
    gotoCreatePath,
    documentForm,
    gotoUpdatePath,
    routePathname,
    userDefaultSpace,
  } = props;

  const fetch_documents = () => {
    dispatch(listDocumentsThunk());
  };

  useEffect(() => {
    if (routePathname === '/dashboard/documents') {
      toggleDocumentForm({
        showForm: false,
        data: {},
      });

      fetch_documents();

      if (Boolean(documentDetails?.id)) {
        dispatch(documentDetailsThunk(documentDetails?.id));
      }
    }
    window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routePathname]);

  useEffect(() => {
    fetch_documents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const handleDisplayInformation = (document_id) => {
    setDocumentId(document_id);

    dispatch(documentDetailsThunk(document_id));
  };

  const handleFullTextSearch = (event) => {
    event.preventDefault();
    setSearchQuery(event.target.value);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    navigate(`?search=${searchQuery}`);
    setIsSearching(true);
    dispatch(fullTextSearchThunk(searchQuery));
  };


  useEffect(() => {
    const fetchExportedDocumentUrl = async () => {
      try {
        const response = await axios.get(`${API}/dashboard/document-exports`);
        const exportData = response?.data;

        if (exportData?.download_url) {
          setExportedDocumentUrl(exportData?.download_url);
        }

      } catch (error) {
        console.log("error: ", error);
      }
    };

    fetchExportedDocumentUrl();
  }, []);

  const exportDocuments = async () => {
    try {
      setIsExporting(true);

      const response = await axios.post(`${API}/dashboard/document-exports`);
  
      if (response.status === 200 || response.status === 201) {
        dispatch(
          setMsgInfo({
            success: true,
            msg: [tr('Document export started successfully. You will receive an email when it is ready.')],
          })
        );
      }
    } catch (error) {
      const errs = getResError(error);
      dispatch(
        setMsgInfo({
          success: false,
          msg: errs,
        })
      );
    } finally {
      setIsExporting(false);
    }
  };

  const downloadExportedDocument = () => {
    if (exportedDocumentUrl) {
      window.open(exportedDocumentUrl, '_blank');
    }
  };

  const configurePostboxes = () => setShowPostboxConfiguration(true);

  const extractPostboxes = async () => {
    setIsExtractingPostboxes(true);
    const results = [];
    try {
      const credentials = await listPostboxCredentials();
      if (!credentials.length) {
        setShowPostboxConfiguration(true);
        dispatch(setMsgInfo({
          success: false,
          msg: [tr('Configure at least one postbox before extracting documents.')],
        }));
        return;
      }

      for (const credential of credentials) {
        try {
          let result = await extractPostboxDocuments(credential.id);
          if (result.requires_2fa) {
            const otp = window.prompt(
              `${credential.description}: ${tr('Enter the one-time confirmation code from your other device.')}`,
            );
            if (!otp) {
              result = {
                credential_id: credential.id,
                description: credential.description,
                reached: false,
                documents_transferred: 0,
                duplicates_skipped: 0,
                errors: [tr('Two-factor confirmation was cancelled.')],
              };
            } else {
              result = await extractPostboxDocuments(
                credential.id,
                otp,
                result.challenge_token,
              );
            }
          }
          results.push(result);
        } catch (error) {
          results.push(error?.response?.data || {
            credential_id: credential.id,
            description: credential.description,
            reached: false,
            documents_transferred: 0,
            duplicates_skipped: 0,
            errors: [tr('The postbox extraction failed.')],
          });
        }
      }

      setPostboxResults(results);
      setShowPostboxSummary(true);
      fetch_documents();
    } catch (error) {
      dispatch(setMsgInfo({
        success: false,
        msg: [tr('Could not load postbox configurations.')],
      }));
    } finally {
      setIsExtractingPostboxes(false);
    }
  };


  const handleReset = () => {
    setSearchQuery('');
    navigate(`/dashboard/documents`);
    setIsSearching(false);
  };

  const handleEdit = () => {
    if (documentDetails?.id) {
      gotoUpdatePath(documentDetails?.id);
      toggleDocumentForm({ data: { ...documentDetails }, showForm: true });
    }
  };

  const handleDelete = () => {
    setToggleModal((toggleModal) => !toggleModal);
  };

  const handleToggleViewDocumentModal = () => {
    setToggleViewDocumentModalModal(
      (toggleViewDocumentModal) => !toggleViewDocumentModal,
    );
  };
  const handleViewDocument = () => {
    handleToggleViewDocumentModal();
  };

  const handleConfirmDelete = async () => {
    try {
      setIsDeleting(true);
      const response = await deleteDocumentService(documentId);
      setToggleModal((toggleModal) => !toggleModal);
      dispatch(
        setMsgInfo({
          success: true,
          msg: [`${tr('Document deleted successfully')}`],
        }),
      );

      if (response.status === 204) {
        fetch_documents();
      }

      setIsDeleting(false);
    } catch (error) {
      setIsDeleting(false);
      const errors = filterError(error);

      dispatch(
        setMsgInfo({
          success: false,
          msg: [`${tr('Document could not be deleted')} ${errors}`],
        }),
      );

      throw error;
    }
  };

  const handleToggleModal = () => {
    setToggleModal((toggleModal) => !toggleModal);
  };

  const showFormPage = () => {
    gotoCreatePath();
    dispatch(documentClearDetailsThunk());
    toggleDocumentForm({ showForm: true });
  };

  const showDocumentPage = () => {
    fetch_documents();
  };

  const documentPage = (
    <div className="main-container-documentPage container-margin_documentPage">
      <div className="p-row_documentPage">
        <div className="cls-3_documentPage rightArea_documentPage">
          <div className="filterAreaContent_documentPage">
            <FilterCriteria />
          </div>
        </div>

        <div className="cls-6_documentPage mainDocumentArea_documentPage">
          <div className="mainDocumentAreaContainer_documentPage">
            <div className="search_documentPage">
              <form
                action="/"
                method="get"
                onSubmit={handleSubmit}
                onReset={handleReset}
              >
                <label>
                  <span className="visually-hidden">{tr('Full text search')}</span>
                </label>

                <div className="searchInput_documentPage">
                  <input
                    type="text"
                    name="search"
                    value={searchQuery}
                    placeholder={tr('Full text search')}
                    onChange={handleFullTextSearch}
                  />

                  {searchQuery && (
                    <Button
                      type="reset"
                      className={'seachReset_button_documentPage'}
                      name={'X'}
                    >
                      X
                    </Button>
                  )}
                </div>

                <Button
                  type="submit"
                  name={tr('Search')}
                  className={'seachButton_documentPage'}
                />
              </form>
            </div>

            <div className="dropZone_documentPage">
              <Dropzone {...props} showFormPage={showFormPage} />
            </div>

            <div className="listDocuments_documentPage">
              <ListDocuments
                isSearching={isSearching}
                document_id={documentId}
                handleDisplayInformation={handleDisplayInformation}
                handleDelete={handleDelete}
                handleEdit={handleEdit}
                handleViewDocument={handleViewDocument}
              />
            </div>
          </div>
        </div>

        <div className="cls-3_documentPage rightArea_documentPage">
          <div className="documentActions_documentPage">
          <Dropdown
              options={[
                {
                  name: 'Export Documents',
                  onClick: exportDocuments,
                  isLink: false,
                },
                {
                  name: 'Configure Postbox extractions',
                  onClick: configurePostboxes,
                  isLink: false,
                },
                {
                  name: 'Extract Postbox documents',
                  onClick: extractPostboxes,
                  isLink: false,
                },
                ...(exportedDocumentUrl ? [{
                  name: 'Download Exported Document', 
                  onClick: downloadExportedDocument,
                  isLink: false,
                }] : [])
              ]}
              tr={tr}
              headerTitle={'Document Actions'}
              userDefaultSpace={userDefaultSpace}
              loading={isExporting || isExtractingPostboxes}
              type={'vertical'}
            />
          </div>
          <div className="documentInfoAreaContainer_documentPage">
            <div className="documentInfoHeaderText_documentPage">
              {tr('File Details')}
            </div>

            <div className="documentInfoBody_documentPage">
              <DocumentInfo />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const formPage = <DocumentForm {...props} showDocumentPage={showDocumentPage} />;

  return (
    <DashboardTemplate
      {...props}
      routes={routes}
      redirects={redirects}
      render={() => (
        <MainWrapper hasSideBar>
          <Container>
            {/* {showScreen[pageState]} */}
            {documentForm.showForm ? formPage : documentPage}

            <Modal
              toggleModal={toggleModal}
              title={tr('Delete Document')}
              warning
              isSubmitting={isDeleting}
              buttonTitle={tr('DELETE')}
              handleConfrimDelete={handleConfirmDelete}
              handleToggleModal={handleToggleModal}
              message={tr('Are you sure you want to delete this Document?')}
            />

            <Modal
              toggleModal={toggleViewDocumentModal}
              handleToggleModal={handleToggleViewDocumentModal}
              children={<div>Children</div>}
            />

            <PostboxConfiguration
              open={showPostboxConfiguration}
              onClose={() => setShowPostboxConfiguration(false)}
              tr={tr}
            />

            <PostboxExtractionSummary
              open={showPostboxSummary}
              onClose={() => setShowPostboxSummary(false)}
              results={postboxResults}
              tr={tr}
            />
          </Container>
        </MainWrapper>
      )}
    />
  );
};

export default compose(
  withDocument,
  withTranslation,
  withPersonalInfo,
  withCustomRouter,
  withUserDefaultSpace,
)(Documents);
